import { useCallback } from 'react';

/**
 * Dual-thumb range slider — both inputs share the same min/max so thumb
 * positions match the visual track. minRange is enforced in onChange only.
 */
export default function VideoRangeSlider({
    min = 0,
    max,
    step = 0.1,
    minRange = 0.2,
    value,
    onChange,
    startLabel,
    endLabel,
}) {
    const [start, end] = value;
    const safeMax = Math.max(min, max);
    const span = Math.max(safeMax - min, 0.001);
    const startPct = ((start - min) / span) * 100;
    const endPct = ((end - min) / span) * 100;

    const emitChange = useCallback((nextStart, nextEnd, thumb) => {
        onChange([nextStart, nextEnd], thumb);
    }, [onChange]);

    const handleStartChange = (e) => {
        const raw = Number(e.target.value);
        const nextStart = Math.max(min, Math.min(raw, end - minRange));
        emitChange(nextStart, end, 'start');
    };

    const handleEndChange = (e) => {
        const raw = Number(e.target.value);
        const nextEnd = Math.min(safeMax, Math.max(raw, start + minRange));
        emitChange(start, nextEnd, 'end');
    };

    return (
        <div className="space-y-2">
            <div className="relative h-8 px-1">
                <div className="absolute left-0 right-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[#ECE8E4] dark:bg-zinc-700" />
                <div
                    className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-violet-500"
                    style={{
                        left: `${startPct}%`,
                        width: `${Math.max(0, endPct - startPct)}%`,
                    }}
                />
                <input
                    type="range"
                    min={min}
                    max={safeMax}
                    step={step}
                    value={start}
                    onChange={handleStartChange}
                    className="video-range-thumb video-range-thumb-start absolute inset-0 z-20 h-full w-full"
                    aria-label={startLabel || 'Start'}
                />
                <input
                    type="range"
                    min={min}
                    max={safeMax}
                    step={step}
                    value={end}
                    onChange={handleEndChange}
                    className="video-range-thumb video-range-thumb-end absolute inset-0 z-30 h-full w-full"
                    aria-label={endLabel || 'End'}
                />
            </div>

            <div className="flex items-center justify-between gap-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                <span>{startLabel}</span>
                <span>{endLabel}</span>
            </div>
        </div>
    );
}
